import { useAlert } from '@traba/context'
import { theme } from '@traba/theme'
import { useCallback, useEffect, useState } from 'react'
import { Col } from './Col'
import { Input } from './Input/Input.styles'
import Row from './Row'
import { SvgIcon } from './SvgIcon'
import { Text } from './Text'

interface SingleFileDropProps {
  id?: string
  upload: File | undefined
  setUpload: (file: File | undefined) => void
  multiple?: false
  accept?: string
  maxSize?: number
  style?: React.CSSProperties
}

interface MultiFileDropProps {
  id?: string
  uploads: File[]
  setUploads: React.Dispatch<React.SetStateAction<File[]>>
  multiple: true
  accept?: string
  maxSize?: number
  style?: React.CSSProperties
}

type DropZoneProps = SingleFileDropProps | MultiFileDropProps

const rowStyling = {
  backgroundColor: theme.colors.Violet10,
  borderStyle: 'dashed',
  borderRadius: theme.space.xs,
  borderWidth: 2,
  padding: theme.space.med,
}

// Helper to create a unique identifier for a file based on its properties
const getFileId = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`

export const FileDrop: React.FC<DropZoneProps> = (props) => {
  const { id, accept, maxSize = 20971520, style } = props
  const isMultiple = props.multiple === true

  const { showError } = useAlert()
  const [generatedUniqueId, setGeneratedUniqueId] = useState<
    string | undefined
  >(undefined)
  useEffect(() => {
    setGeneratedUniqueId(
      id ? id : `file-input-${Math.random().toString(36).substring(2, 15)}`,
    )
  }, [id])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const validateFile = useCallback(
    (file: File): boolean => {
      if (file.size > maxSize) {
        showError(`File size must be less than ${maxSize / 1024 / 1024}MB`)
        return false
      }
      return true
    },
    [maxSize, showError],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const droppedFiles = e.dataTransfer.files
      if (!droppedFiles || droppedFiles.length === 0) {
        return
      }

      if (isMultiple) {
        const multiProps = props as MultiFileDropProps
        const validFiles = Array.from(droppedFiles).filter(validateFile)
        if (validFiles.length > 0) {
          // Use functional update to avoid stale closure issues with rapid file additions
          // Also deduplicate by checking if file already exists in the list
          multiProps.setUploads((prev) => {
            const existingIds = new Set(prev.map(getFileId))
            const newFiles = validFiles.filter(
              (f) => !existingIds.has(getFileId(f)),
            )
            return [...prev, ...newFiles]
          })
        }
      } else {
        const singleProps = props as SingleFileDropProps
        const file = droppedFiles[0]
        if (file && validateFile(file)) {
          singleProps.setUpload(file)
        }
      }
    },
    [props, isMultiple, validateFile],
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (!selectedFiles || selectedFiles.length === 0) {
        return
      }

      if (isMultiple) {
        const multiProps = props as MultiFileDropProps
        const validFiles = Array.from(selectedFiles).filter(validateFile)
        if (validFiles.length > 0) {
          // Use functional update to avoid stale closure issues with rapid file additions
          // Also deduplicate by checking if file already exists in the list
          multiProps.setUploads((prev) => {
            const existingIds = new Set(prev.map(getFileId))
            const newFiles = validFiles.filter(
              (f) => !existingIds.has(getFileId(f)),
            )
            return [...prev, ...newFiles]
          })
        }
      } else {
        const singleProps = props as SingleFileDropProps
        const file = selectedFiles[0]
        if (file && validateFile(file)) {
          singleProps.setUpload(file)
        }
      }
      // Reset input so the same file can be selected again
      e.target.value = ''
    },
    [props, isMultiple, validateFile],
  )

  const handleRemoveFile = useCallback(
    (index?: number) => {
      if (isMultiple) {
        const multiProps = props as MultiFileDropProps
        if (index !== undefined) {
          // Use functional update to avoid stale closure issues with rapid file removals
          multiProps.setUploads((prev) => prev.filter((_, i) => i !== index))
        }
      } else {
        const singleProps = props as SingleFileDropProps
        singleProps.setUpload(undefined)
      }
    },
    [props, isMultiple],
  )

  // Get current files for rendering
  const currentFiles = isMultiple
    ? (props as MultiFileDropProps).uploads
    : (props as SingleFileDropProps).upload
      ? [(props as SingleFileDropProps).upload]
      : []

  const hasFiles = currentFiles.length > 0

  // In single-file mode, hide the drop zone when a file is selected (original behavior)
  // In multi-file mode, always show the drop zone to allow adding more files
  const showDropZone = isMultiple || !hasFiles

  return (
    <Col
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      gap={hasFiles && showDropZone ? theme.space.xxs : undefined}
      style={{
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      {hasFiles &&
        currentFiles.map((file, index) => (
          <Row
            key={`${file?.name}-${file?.size}-${file?.lastModified}`}
            justifyBetween
            style={{
              ...rowStyling,
              backgroundColor: theme.colors.Grey10,
              borderColor: theme.colors.Grey80,
              padding: theme.space.xs,
              ...style,
            }}
          >
            <Row gap={theme.space.xxs}>
              <SvgIcon
                name={'simpleMessage'}
                color={theme.colors.MidnightBlue}
              />
              <Text variant="h6">{file?.name}</Text>
            </Row>
            <SvgIcon
              name={'trash'}
              color={theme.colors.MidnightBlue}
              onClick={() => handleRemoveFile(isMultiple ? index : undefined)}
            />
          </Row>
        ))}
      {showDropZone && (
        <label htmlFor={generatedUniqueId}>
          <Row
            style={{
              ...rowStyling,
              backgroundColor: theme.colors.Violet10,
              borderColor: theme.colors.Violet60,
              ...style,
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Text variant="h6">
              {isMultiple
                ? hasFiles
                  ? 'Add more files'
                  : 'Select or drop files'
                : 'Select or drop a file'}
            </Text>
            <Input
              id={generatedUniqueId}
              type="file"
              accept={accept}
              multiple={isMultiple}
              className="hidden"
              onChange={(e) =>
                handleFileInput(e as React.ChangeEvent<HTMLInputElement>)
              }
              style={{ display: 'none' }}
            />
          </Row>
        </label>
      )}
    </Col>
  )
}
